import { useEffect, useRef } from "react";

const W = 380;
const H = 460;

const GREENS = ["#2E5230", "#3F6B3A", "#4E7A42", "#5C8A4E", "#6E9A57", "#8FAE5D", "#A9CB7E"];
const OLIVE = ["#4A6350", "#587360", "#668370", "#7A9480", "#8FA690", "#A3B8A0", "#B8CBB2"];
const BARK = ["#4E3B28", "#57422E", "#604A34", "#684F37", "#6B5138"];
const GOLD = "#C8A24A";

const CFG = {
  A: {
    seed: 4111, trunkLen: 92, trunkW: 26, lean: 0.14, rootKids: 3, spread: 1.05,
    lenRatio: 0.72, maxDepth: 6, minWidth: 0.8, bend: 0.5, flat: true,
    leafR: 28, leafN: 22, squash: 0.45, palette: GREENS,
  },
  B: {
    seed: 9201, trunkLen: 82, trunkW: 31, lean: 0.03, rootKids: 4, spread: 0.85,
    lenRatio: 0.68, maxDepth: 6, minWidth: 0.8, bend: 0.35,
    leafR: 27, leafN: 20, squash: 0.9, palette: GREENS,
  },
  C: {
    seed: 7333, trunkLen: 84, trunkW: 21, lean: 0.0, rootKids: 3, spread: 0.75,
    lenRatio: 0.69, maxDepth: 7, minWidth: 0.5, bend: 0.3, droop: true,
    leafR: 14, leafN: 10, squash: 0.85, hang: 7, palette: OLIVE,
  },
};

const drawLeaf = (ctx, x, y, size, rot, c1, c2) => {
  const s = size / 9;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(s, s);
  const grad = ctx.createLinearGradient(0, -9, 0, 9);
  grad.addColorStop(0, c1);
  grad.addColorStop(1, c2);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(0, -9);
  ctx.bezierCurveTo(7, -5, 6, 5, 0, 9);
  ctx.bezierCurveTo(-6, 5, -7, -5, 0, -9);
  ctx.fill();
  ctx.restore();
};

const shade = (hex, amt) => {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `rgb(${r},${g},${b})`;
};

function render(ctx, cfg) {
  let seed = cfg.seed;
  const rng = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  /* ---- skeleton ---- */
  const branches = [];
  const terminals = [];
  const build = (parent, len, width, angle, depth) => {
    const b = { parent, len, width, angle, depth, bend: (rng() - 0.5) * cfg.bend, x: 0, y: 0, ex: 0, ey: 0, abs: 0 };
    const id = branches.length;
    branches.push(b);
    if (depth >= cfg.maxDepth || width < cfg.minWidth) {
      terminals.push(id);
      return;
    }
    const kids = depth === 0 ? cfg.rootKids : depth < 3 ? (rng() > 0.4 ? 3 : 2) : 2;
    for (let i = 0; i < kids; i++) {
      const spread =
        (kids === 1 ? 0 : (i / (kids - 1) - 0.5)) * (cfg.spread + rng() * 0.4) + (rng() - 0.5) * 0.2;
      build(id, len * (cfg.lenRatio + rng() * 0.12), width * (0.55 + rng() * 0.1), spread, depth + 1);
    }
  };
  build(null, cfg.trunkLen, cfg.trunkW, -Math.PI / 2 + cfg.lean, 0);

  /* ---- positions with variant character ---- */
  for (const b of branches) {
    let baseX, baseY, parentAbs;
    if (b.parent === null) {
      baseX = W / 2;
      baseY = H - 46;
      parentAbs = 0;
      b.abs = b.angle;
    } else {
      const p = branches[b.parent];
      baseX = p.ex;
      baseY = p.ey;
      b.abs = p.abs + b.angle;
    }
    void parentAbs;
    if (cfg.flat && b.depth >= 2) {
      // amplify outward spread, clamp near-vertical growth → flat wide crown
      let dev = b.abs + Math.PI / 2;
      dev = Math.max(-1.4, Math.min(1.4, dev * 1.35));
      b.abs = -Math.PI / 2 + dev;
    }
    if (cfg.droop && b.depth >= 4) {
      // tips bend down under gravity
      const down = Math.cos(b.abs) >= 0 ? Math.PI * 0.42 : Math.PI * 0.58;
      const k = Math.min(0.4, 0.13 * (b.depth - 3));
      b.abs = b.abs + (down - b.abs) * k;
    }
    b.x = baseX;
    b.y = baseY;
    b.ex = baseX + Math.cos(b.abs) * b.len;
    b.ey = baseY + Math.sin(b.abs) * b.len;
  }

  /* ---- ground shadow + grass ---- */
  const gs = ctx.createRadialGradient(W / 2, H - 40, 6, W / 2, H - 40, 105);
  gs.addColorStop(0, "rgba(18,42,29,0.18)");
  gs.addColorStop(1, "rgba(18,42,29,0)");
  ctx.save();
  ctx.translate(W / 2, H - 40);
  ctx.scale(1, 0.14);
  ctx.translate(-W / 2, -(H - 40));
  ctx.fillStyle = gs;
  ctx.beginPath();
  ctx.arc(W / 2, H - 40, 105, 0, 6.29);
  ctx.fill();
  ctx.restore();
  ctx.strokeStyle = "rgba(94,128,84,0.6)";
  ctx.lineWidth = 1;
  for (let i = 0; i < 24; i++) {
    const gx = W / 2 + (rng() - 0.5) * 190;
    const gy = H - 40 + (rng() - 0.5) * 10;
    ctx.beginPath();
    ctx.moveTo(gx, gy);
    ctx.quadraticCurveTo(gx + (rng() - 0.5) * 4, gy - 5 - rng() * 6, gx + (rng() - 0.5) * 8, gy - 8 - rng() * 8);
    ctx.stroke();
  }

  /* ---- limbs (tapered polygons), bark streaks ---- */
  for (const b of branches) {
    const dx = Math.cos(b.abs);
    const dy = Math.sin(b.abs);
    const nx = -dy;
    const ny = dx;
    const mx = b.x + dx * b.len * 0.5 + nx * b.len * b.bend * 0.15;
    const my = b.y + dy * b.len * 0.5 + ny * b.len * b.bend * 0.15;
    if (b.depth <= 3) {
      const w0 = b.width / 2;
      const w1 = Math.max((b.width * 0.6) / 2, 0.5);
      const wm = (w0 + w1) * 0.55;
      ctx.fillStyle = BARK[b.depth];
      ctx.beginPath();
      ctx.moveTo(b.x + nx * w0, b.y + ny * w0);
      ctx.quadraticCurveTo(mx + nx * wm, my + ny * wm, b.ex + nx * w1, b.ey + ny * w1);
      ctx.lineTo(b.ex - nx * w1, b.ey - ny * w1);
      ctx.quadraticCurveTo(mx - nx * wm, my - ny * wm, b.x - nx * w0, b.y - ny * w0);
      ctx.closePath();
      ctx.fill();
      if (b.depth <= 1) {
        ctx.strokeStyle = "rgba(35,25,12,0.28)";
        ctx.lineWidth = 1;
        for (const f of [-0.35, 0.2]) {
          ctx.beginPath();
          ctx.moveTo(b.x + nx * w0 * f, b.y + ny * w0 * f);
          ctx.quadraticCurveTo(mx + nx * wm * f, my + ny * wm * f, b.ex + nx * w1 * f, b.ey + ny * w1 * f);
          ctx.stroke();
        }
      }
    } else {
      ctx.strokeStyle = b.depth === 4 ? "#6B5138" : "#75603F";
      ctx.lineWidth = Math.max(b.width, 0.6);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(b.x, b.y);
      ctx.quadraticCurveTo(mx, my, b.ex, b.ey);
      ctx.stroke();
    }
  }

  /* ---- foliage clumps with directional light shading ---- */
  const Lx = -0.55;
  const Ly = -0.83;
  const pal = cfg.palette;
  for (const ti of terminals) {
    const b = branches[ti];
    for (let i = 0; i < cfg.leafN; i++) {
      const a = rng() * 6.28;
      const d = Math.sqrt(rng()) * cfg.leafR;
      const ox = Math.cos(a) * d;
      const oy = Math.sin(a) * d * cfg.squash + (cfg.hang ? cfg.hang + rng() * 8 : 0);
      const lum = -(ox * Lx + oy * Ly) / cfg.leafR; // lit side → higher
      let idx = Math.round(3 + lum * 2.4 + (rng() - 0.5) * 1.4);
      idx = Math.max(0, Math.min(pal.length - 1, idx));
      const gold = rng() < 0.045;
      const c1 = gold ? GOLD : pal[idx];
      const c2 = gold ? "#96762C" : shade(pal[idx], -28);
      drawLeaf(ctx, b.ex + ox, b.ey + oy, 3.4 + rng() * 4, rng() * 6.28, c1, c2);
    }
  }
}

export const StaticTree = ({ variant = "B" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    render(ctx, CFG[variant]);
  }, [variant]);
  return <canvas ref={ref} style={{ width: "100%", maxWidth: W, height: "auto" }} />;
};
